import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import type { OidcGroupMappingDto } from '@shared/services/apiService';

import type { SsoMappingsApi } from '../model/useSsoMappingsApi';
import { useStyles } from './styles';

interface DeleteSsoMappingDialogProps {
  mapping: OidcGroupMappingDto | null;
  sso: SsoMappingsApi;
  onClose: () => void;
}

export const DeleteSsoMappingDialog: React.FC<DeleteSsoMappingDialogProps> = ({
  mapping,
  sso,
  onClose,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();
  const [isDeleting, setIsDeleting] = useState(false);

  // Without any mapping nobody of this company can sign in through SSO any more.
  const isLast = sso.mappings.length === 1;

  const handleDelete = async () => {
    if (!mapping) return;
    try {
      setIsDeleting(true);
      await sso.removeMapping(mapping.id);
      onClose();
    } catch (error) {
      showError({ title: t('accessControl.ssoMappings.errors.deleteTitle'), error });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={Boolean(mapping)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>{t('accessControl.ssoMappings.delete.title')}</DialogTitle>
      <DialogContent dividers className={classes.modalContent}>
        <Typography>
          {t('accessControl.ssoMappings.delete.confirmation', {
            group: mapping?.groupValue,
            permissionGroup: mapping?.permissionGroup.name,
          })}
        </Typography>
        <Typography variant="body2" color={isLast ? 'error' : 'text.secondary'}>
          {t(
            isLast
              ? 'accessControl.ssoMappings.delete.lastWarning'
              : 'accessControl.ssoMappings.delete.warning',
          )}
        </Typography>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('accessControl.common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={isDeleting}
          data-testid="sso-mapping-delete-confirm">
          {isDeleting ? t('accessControl.common.deleting') : t('accessControl.common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
